import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ParticleField } from "./ParticleField";
import { MaskLines } from "./motion";

// Dynamic lucide icon
export const Icon = ({ name, ...props }) => {
  const Cmp = Icons[name] || Icons.Circle;
  return <Cmp {...props} />;
};

// Section eyebrow label
export const Eyebrow = ({ children, className = "", light = false }) => (
  <span className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] ${light ? "text-gold-light" : "text-gold"} ${className}`}>
    <span className="h-px w-8 bg-gold/60" />
    {children}
  </span>
);

// Italic-gold accent title helper: pass array of {t, gold?}
export const AccentTitle = ({ parts, className = "" }) => (
  <span className={className}>
    {parts.map((p, i) =>
      p.gold ? (
        <em key={i} className="not-italic text-gold-gradient font-display italic">{p.t}</em>
      ) : (
        <span key={i}>{p.t}</span>
      )
    )}
  </span>
);

// Slow editorial marquee
export const Marquee = ({ items, className = "", stroke = true }) => (
  <div className={`overflow-hidden whitespace-nowrap ${className}`} aria-hidden="true">
    <div className="inline-flex animate-marquee">
      {[0, 1].map((rep) => (
        <div key={rep} className="inline-flex items-center">
          {items.map((it, i) => (
            <span key={`${rep}-${i}`} className={`font-display text-5xl sm:text-7xl font-semibold mx-8 ${stroke ? "text-stroke" : "text-white/90"}`}>
              {it}
              <span className="text-gold mx-8">✦</span>
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

// Reusable page hero (dark, with particles + parallax image option)
export const PageHero = ({ eyebrow, titleLines, subtitle, image }) => (
  <section className="relative min-h-[62vh] flex items-end overflow-hidden bg-night pt-32 pb-16" data-testid="page-hero">
    {image && (
      <div className="absolute inset-0">
        <img src={image} alt="" className="h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-night via-night/80 to-night/40" />
      </div>
    )}
    <ParticleField className="absolute inset-0 h-full w-full" density={40} />
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8 w-full">
      {eyebrow && <Eyebrow light className="mb-5">{eyebrow}</Eyebrow>}
      <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-medium tracking-tight text-white leading-[1.02] max-w-4xl">
        <MaskLines lines={titleLines} />
      </h1>
      {subtitle && (
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mt-6 max-w-2xl text-lg text-white/70 font-body leading-relaxed">
          {subtitle}
        </motion.p>
      )}
    </div>
  </section>
);
